import React from "react";
import { Bus, CircleDollarSign, Landmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettlementSummaryBar } from "../../settlement/components/SettlementSummaryBar";
import { StatCard } from "../../settlement/components/StatCard";
import {
  PartnerHotelRevenueRank,
  PartnerTotalRevenueSummary,
} from "@/types/partner-dashboard";

const statBadgeClassName =
  "inline-flex max-w-full w-fit items-center justify-center rounded-md bg-royal-glow-5 px-2 py-0.5 text-2xs font-semibold leading-none text-royal-glow-2";

function getCurrencySymbol(currency: string): string {
  if (currency === "EUR") return "€";
  if (currency === "USD") return "$";
  if (currency === "GBP") return "£";
  return `${currency} `;
}

function formatRevenueValue(value: number, currency: string): string {
  return `${getCurrencySymbol(currency)}${value.toLocaleString("en-GB")}`;
}

type GuesteriaPartnersRevenueStatsProps = {
  topRevenueHotel: PartnerHotelRevenueRank | null;
  lowestRevenueHotel: PartnerHotelRevenueRank | null;
  totalRevenueSummary: PartnerTotalRevenueSummary | null;
};

export const GuesteriaPartnersRevenueStats = ({
  topRevenueHotel,
  lowestRevenueHotel,
  totalRevenueSummary,
}: GuesteriaPartnersRevenueStatsProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <SettlementSummaryBar>
      <StatCard
        icon={<Landmark className="size-6 text-light-stone" />}
        label={topRevenueHotel?.hotelName ?? "-"}
        value={
          topRevenueHotel
            ? formatRevenueValue(
                topRevenueHotel.totalRevenue,
                totalRevenueSummary?.currency ?? "EUR",
              )
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.highestRevenueMonth")}
          </span>
        }
      />
      <StatCard
        icon={<CircleDollarSign className="size-6 text-light-stone" />}
        label="Total revenue by hotels"
        value={
          totalRevenueSummary
            ? formatRevenueValue(
                totalRevenueSummary.totalRevenue,
                totalRevenueSummary.currency ?? "EUR",
              )
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.forMonth")}
          </span>
        }
      />
      <StatCard
        icon={<Bus className="size-6 text-light-stone" />}
        label={lowestRevenueHotel?.hotelName ?? "-"}
        value={
          lowestRevenueHotel
            ? formatRevenueValue(
                lowestRevenueHotel.totalRevenue,
                totalRevenueSummary?.currency ?? "EUR",
              )
            : "-"
        }
        badgeNode={
          <span className={statBadgeClassName}>
            {t("billing.guesteriaPartnersRevenue.badge.lowestRevenue")}
          </span>
        }
      />
    </SettlementSummaryBar>
  );
};
